import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAppSelector } from 'store';
import { resolveBackOfficeWsUrl } from './resolveBackOfficeWsUrl';

export type RealtimeMessage = {
  type: string;
  data?: Record<string, unknown>;
};

type Handler = (data: Record<string, unknown>, message: RealtimeMessage) => void;

type TopicSubscription = {
  topic: string;
  resourceId: string;
};

type RealtimeClient = {
  accepted: boolean;
  send: (type: string, data: Record<string, unknown>) => boolean;
  subscribe: (type: string, handler: Handler) => () => void;
  subscribeResource: (topic: string, resourceId: string) => () => void;
};

const reconnectInterval = (attempt: number) => Math.min(1000 * 2 ** attempt, 8000);

const BackOfficeRealtimeContext = createContext<RealtimeClient | null>(null);

function topicKey(topic: string, resourceId: string) {
  return `${topic}:${resourceId}`;
}

function useBackOfficeRealtimeSocket(): RealtimeClient {
  const isAuthorized = useAppSelector((state) => state.auth.isAuthorized);
  const publicId = useAppSelector((state) => state.auth.info?.publicId);
  const socketRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef<Map<string, Set<Handler>>>(new Map());
  const topicsRef = useRef<Map<string, TopicSubscription>>(new Map());
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const acceptedRef = useRef(false);
  const [accepted, setAccepted] = useState(false);
  const publicIdRef = useRef(publicId);
  const authorizedRef = useRef(isAuthorized);

  publicIdRef.current = publicId;
  authorizedRef.current = isAuthorized;

  const send = useCallback((type: string, data: Record<string, unknown>) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return false;
    }
    socket.send(JSON.stringify({ type, data }));
    return true;
  }, []);

  const flushTopicSubscriptions = useCallback(() => {
    if (!acceptedRef.current) {
      return;
    }
    Array.from(topicsRef.current.values()).forEach((subscription) => {
      send('subscribe', { topic: subscription.topic, runId: subscription.resourceId });
    });
  }, [send]);

  const subscribe = useCallback((type: string, handler: Handler) => {
    const existing = handlersRef.current.get(type) ?? new Set<Handler>();
    existing.add(handler);
    handlersRef.current.set(type, existing);
    return () => {
      const current = handlersRef.current.get(type);
      if (!current) {
        return;
      }
      current.delete(handler);
      if (current.size === 0) {
        handlersRef.current.delete(type);
      }
    };
  }, []);

  const subscribeResource = useCallback((topic: string, resourceId: string) => {
    const normalizedTopic = topic.trim();
    const normalizedId = resourceId.trim();
    if (!normalizedTopic || !normalizedId) {
      return () => undefined;
    }
    const key = topicKey(normalizedTopic, normalizedId);
    topicsRef.current.set(key, { topic: normalizedTopic, resourceId: normalizedId });
    if (acceptedRef.current) {
      send('subscribe', { topic: normalizedTopic, runId: normalizedId });
    }
    return () => {
      topicsRef.current.delete(key);
      send('unsubscribe', { topic: normalizedTopic, runId: normalizedId });
    };
  }, [send]);

  useEffect(() => {
    const wsUrl = resolveBackOfficeWsUrl();
    const token = localStorage.getItem('token');
    const scopedPublicId = typeof publicId === 'string' ? publicId.trim() : '';
    if (!isAuthorized || !wsUrl || !token || !scopedPublicId) {
      return undefined;
    }

    let cancelled = false;
    let authRejected = false;

    const clearReconnect = () => {
      if (reconnectTimerRef.current != null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const detachSocket = (socket: WebSocket | null) => {
      if (!socket) {
        return;
      }
      socket.onopen = null;
      socket.onmessage = null;
      socket.onclose = null;
      socket.onerror = null;
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
    };

    const canReconnect = () => (
      !cancelled
      && !authRejected
      && Boolean(authorizedRef.current)
      && Boolean(publicIdRef.current?.trim())
      && Boolean(localStorage.getItem('token'))
    );

    const emit = (type: string, data: Record<string, unknown>, message: RealtimeMessage) => {
      const handlers = handlersRef.current.get(type);
      if (!handlers?.size) {
        return;
      }
      handlers.forEach((handler) => handler(data, message));
    };

    const connect = () => {
      if (!canReconnect()) {
        return;
      }
      clearReconnect();
      const previous = socketRef.current;
      socketRef.current = null;
      detachSocket(previous);

      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;
      acceptedRef.current = false;
      setAccepted(false);

      socket.onopen = () => {
        reconnectAttemptRef.current = 0;
        const currentPublicId = publicIdRef.current?.trim();
        const currentToken = localStorage.getItem('token');
        if (!currentPublicId || !currentToken || !authorizedRef.current) {
          socket.close();
          return;
        }
        socket.send(JSON.stringify({
          type: 'attemptConnection',
          data: {
            userId: currentPublicId,
            token: currentToken,
          },
        }));
      };

      socket.onmessage = (event) => {
        let parsed: RealtimeMessage;
        try {
          parsed = JSON.parse(String(event.data));
        } catch (_err) {
          return;
        }
        const type = parsed?.type;
        if (!type) {
          return;
        }
        const payload = (parsed.data || {}) as Record<string, unknown>;
        const eventKey = typeof payload.eventKey === 'string' ? payload.eventKey : '';
        if (type === 'connectionAccepted') {
          acceptedRef.current = true;
          setAccepted(true);
          flushTopicSubscriptions();
        } else if (type === 'error' && (eventKey === 'unauthorized' || eventKey === 'userBlocked')) {
          authRejected = true;
          acceptedRef.current = false;
          setAccepted(false);
          clearReconnect();
        }
        emit(type, payload, parsed);
      };

      socket.onclose = () => {
        if (socketRef.current === socket) {
          socketRef.current = null;
        }
        acceptedRef.current = false;
        setAccepted(false);
        emit('disconnected', {}, { type: 'disconnected' });
        if (!canReconnect()) {
          return;
        }
        reconnectAttemptRef.current += 1;
        reconnectTimerRef.current = window.setTimeout(
          connect,
          reconnectInterval(reconnectAttemptRef.current),
        );
      };
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      const socket = socketRef.current;
      if (socket && socket.readyState === WebSocket.OPEN && acceptedRef.current) {
        return;
      }
      socket?.close();
    };

    connect();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      clearReconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      acceptedRef.current = false;
      setAccepted(false);
      const socket = socketRef.current;
      socketRef.current = null;
      detachSocket(socket);
    };
  }, [flushTopicSubscriptions, isAuthorized, publicId]);

  return useMemo(
    () => ({
      accepted,
      send,
      subscribe,
      subscribeResource,
    }),
    [accepted, send, subscribe, subscribeResource],
  );
}

export function BackOfficeRealtimeProvider({ children }: { children: ReactNode }) {
  const client = useBackOfficeRealtimeSocket();
  return (
    <BackOfficeRealtimeContext.Provider value={client}>
      {children}
    </BackOfficeRealtimeContext.Provider>
  );
}

export function useBackOfficeRealtime() {
  const context = useContext(BackOfficeRealtimeContext);
  if (!context) {
    throw new Error('useBackOfficeRealtime must be used inside BackOfficeRealtimeProvider');
  }
  return context;
}
